import { useEffect, useState } from "react"
import { usePage } from "@inertiajs/react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { csrfToken } from "@/lib/csrf"
import { track } from "@/lib/analytics"
import type { SharedProps } from "@/types"

export default function IntroOfferBanner() {
  const { introOffer } = usePage<SharedProps>().props
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!introOffer) return
    track("intro_offer_shown", { surface: "dashboard_banner", plan_id: introOffer.plan_id })
  }, [introOffer])

  async function handleClaim() {
    if (!introOffer) return
    setLoading(true)
    try {
      const res = await fetch("/dashboard/checkout_sessions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ plan_id: introOffer.plan_id }),
      })
      const { url, error } = (await res.json()) as { url?: string; error?: string }
      if (url) {
        track("intro_offer_claimed", { surface: "dashboard_banner", plan_id: introOffer.plan_id })
        window.location.href = url
      } else {
        toast.error(error ?? "Something went wrong")
        setLoading(false)
      }
    } catch {
      toast.error("Something went wrong")
      setLoading(false)
    }
  }

  if (!introOffer) return null

  return (
    <div className="relative mb-6 overflow-hidden rounded-xl border border-red/20 bg-cream/60 shadow-sm">
      <div className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full bg-red/[0.06] blur-2xl" />
      <div className="pointer-events-none absolute -bottom-12 -left-10 size-44 rounded-full bg-red/[0.05] blur-3xl" />

      <div className="relative flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-red">
            Welcome offer · {introOffer.price_label} for {introOffer.video_count} videos
          </p>
          <h2 className="font-display text-xl font-semibold leading-tight text-charcoal sm:text-2xl">
            Your first {introOffer.video_count} videos, sorted today.
          </h2>
          <p className="max-w-2xl text-sm text-warm-grey">
            {introOffer.credits.toLocaleString()} credits for {introOffer.price_label}, one-off. Make them yourself in the storyboard, or email{" "}
            <a
              href="mailto:team@quiteright.co.uk?subject=Make%20my%20first%205%20videos"
              className="font-medium text-red underline-offset-2 hover:text-deep-red hover:underline"
            >
              team@quiteright.co.uk
            </a>{" "}
            and we'll produce them for you.
          </p>
        </div>

        <Button
          onClick={handleClaim}
          disabled={loading}
          className="h-11 shrink-0 self-start bg-red px-5 text-sm font-semibold text-white shadow-sm hover:bg-red/90 sm:self-auto"
        >
          {loading ? "Loading…" : "Claim offer"}
        </Button>
      </div>
    </div>
  )
}
